<template>
  <div class="case-preview-v2" :class="{ 'case-preview-v2--dark': isDarkMode }">
    <div v-if="loading" class="preview-loading d-flex justify-content-center align-items-center vh-100">
      <v-loader />
    </div>

    <template v-else-if="caseData">
      <div class="preview-toolbar hide-print">
        <div
          class="display-mode-toggle"
          role="group"
          :aria-label="$t('travelBook.displayMode')"
        >
          <button
            type="button"
            class="display-mode-toggle__btn"
            :class="{ 'is-active': !isDarkMode }"
            :aria-pressed="!isDarkMode"
            @click="setDisplayMode('color')"
          >
            {{ $t('travelBook.colorMode') }}
          </button>
          <button
            type="button"
            class="display-mode-toggle__btn"
            :class="{ 'is-active': isDarkMode }"
            :aria-pressed="isDarkMode"
            @click="setDisplayMode('dark')"
          >
            {{ $t('travelBook.darkMode') }}
          </button>
        </div>
      </div>
      <!-- 1. HERO: text first (readable), featured image separate below -->
      <header class="preview-hero">
        <div class="preview-hero__intro">
          <div class="preview-body py-24 py-md-64">
            <div class="row align-items-center">
              <div class="col-md-8">
                <p class="preview-hero__kicker mb-12">
                  {{ $t('travelBook.headerTile') }}
                </p>
                <h1 class="preview-hero__title mb-12">{{ caseData.name }}</h1>
                <p
                  v-if="caseData.tour_name && caseData.tour_name !== caseData.name"
                  class="preview-hero__subtitle mb-20"
                >
                  {{ caseData.tour_name }}
                </p>

                <div class="d-flex flex-wrap align-items-center gap-16 preview-hero__meta">
                  <div class="d-flex align-items-center">
                    <span class="mi mi-location me-8" />
                    <span>{{ caseData.origin?.name }}</span>
                    <span class="mi mi-arrow-chevron-forward mx-8 preview-hero__meta-sep" />
                    <span>{{ caseData.destination?.name }}</span>
                  </div>
                  <div class="v-divider"></div>
                  <div class="d-flex align-items-center">
                    <span class="mi mi-calandar me-8" />
                    <span>{{ formatDate(caseData.start_date) }} - {{ formatDate(caseData.end_date_formatted) }}</span>
                  </div>
                </div>

                <div class="d-flex flex-wrap align-items-center gap-20 mt-12 preview-hero__meta preview-hero__meta--secondary">
                  <div class="d-flex align-items-center">
                    <span class="mi mi-three-people me-8" />
                    {{ caseData.pax_count || caseData.paxes?.length || 0 }} {{ $t('general.shared.paxes') }}
                  </div>
                  <div class="d-flex align-items-center">
                    <span :class="getTravelTypeIconClass(caseData.travel_type)" class="me-8" />
                    {{ getTravelTypeLabel(caseData.travel_type) }}
                  </div>
                </div>
              </div>
              <div class="col-md-4 text-center text-md-end mt-32 mt-md-0">
                <svg
                  class="preview-hero__logo-svg mb-20"
                  width="270"
                  height="110"
                  viewBox="0 0 108 44"
                  role="img"
                  aria-label="WAW"
                >
                  <defs>
                    <linearGradient
                      id="wawHeroLogoGrad"
                      gradientUnits="userSpaceOnUse"
                      x1="0"
                      y1="8"
                      x2="108"
                      y2="36"
                      spreadMethod="repeat"
                    >
                      <stop offset="0%" stop-color="#ff6b9d" />
                      <stop offset="25%" stop-color="#e61275" />
                      <stop offset="50%" stop-color="#ffd54a" />
                      <stop offset="75%" stop-color="#26d0a1" />
                      <stop offset="100%" stop-color="#ff6b9d" />
                      <animateTransform
                        attributeName="gradientTransform"
                        type="translate"
                        from="-108 0"
                        to="108 0"
                        dur="4s"
                        repeatCount="indefinite"
                      />
                    </linearGradient>
                  </defs>
                  <path
                    fill="url(#wawHeroLogoGrad)"
                    d="M42.1039 13.1114V22.358C42.1039 27.4828 38.413 32.0696 33.0727 31.999C30.671 31.9636 28.5212 30.9594 26.9801 29.3465C26.9448 29.4183 26.873 29.4901 26.8012 29.5619C25.1529 31.1748 23.002 32.0343 20.7086 31.999C15.7274 31.926 12 27.8761 12 22.6076V13.1114C12 12.5015 12.5015 12 13.0749 12H16.2289C16.8376 12 17.3038 12.5015 17.3038 13.1114V22.4651C17.3038 24.2923 18.1644 26.5151 20.565 26.6587C21.568 26.7305 22.464 26.4432 23.1456 25.7981C23.9344 25.0811 24.3995 23.8991 24.3995 22.6087V13.1114C24.3995 12.5027 24.8657 12 25.4744 12H28.6284C29.2017 12 29.7033 12.5015 29.7033 13.1114V22.4651C29.7033 24.2923 30.5639 26.5151 32.9644 26.6587C33.9675 26.7305 34.8634 26.4432 35.5451 25.7981C36.3339 25.0811 36.7636 23.8991 36.7636 22.6087V13.1114C36.7636 12.5027 37.2651 12 37.875 12H40.9925C41.6012 12 42.1039 12.5015 42.1039 13.1114Z"
                  />
                  <path
                    fill="url(#wawHeroLogoGrad)"
                    d="M62.5845 17.0097C61.6977 15.5199 60.4917 14.315 59.0031 13.4282C57.5098 12.5391 55.8263 12.0889 54.0013 12.0889C52.1764 12.0889 50.5168 12.5403 49.0247 13.4293C47.5349 14.3162 46.33 15.5221 45.4432 17.0108C44.5541 18.504 44.1039 20.1785 44.1039 21.9875C44.1039 23.7964 44.5541 25.4766 45.4432 26.9767C46.33 28.4744 47.5394 29.685 49.0384 30.5718C50.5384 31.4609 52.2174 31.9111 54.0276 31.9111H62.4922C63.2844 31.9111 63.9262 31.2694 63.9262 30.4772V22.0125C63.9262 20.1876 63.4759 18.504 62.5857 17.0108L62.5845 17.0097ZM54.0025 17.1932C55.3327 17.1932 56.4805 17.6662 57.4141 18.5986C58.3465 19.5322 58.8196 20.68 58.8196 22.0103V25.3717C58.8196 26.1639 58.1778 26.8057 57.3856 26.8057H54.0241C52.7076 26.8057 51.5632 26.3292 50.6228 25.3888C49.6824 24.4496 49.2059 23.3052 49.2059 21.9886C49.2059 20.6721 49.679 19.5311 50.6114 18.5986C51.5438 17.6662 52.6848 17.1932 54.0013 17.1932H54.0025Z"
                  />
                  <path
                    fill="url(#wawHeroLogoGrad)"
                    d="M96.03 13.1114V22.358C96.03 27.4828 92.3391 32.0696 86.9989 31.999C84.5972 31.9636 82.4474 30.9594 80.9063 29.3465C80.8709 29.4183 80.7991 29.4901 80.7273 29.5619C79.0791 31.1748 76.9281 32.0343 74.6347 31.999C69.6535 31.9271 65.9261 27.8772 65.9261 22.6099V13.1114C65.9261 12.5027 66.4277 12 67.001 12H70.155C70.7649 12 71.2299 12.5015 71.2299 13.1114V22.4651C71.2299 24.2923 72.0905 26.5151 74.4911 26.6587C75.4942 26.7305 76.3901 26.4432 77.0718 25.7981C77.8605 25.0811 78.3256 23.8991 78.3256 22.6087V13.1114C78.3256 12.5027 78.7918 12 79.4005 12H82.5545C83.1279 12 83.6294 12.5015 83.6294 13.1114V22.4651C83.6294 24.2923 84.49 26.5151 86.8906 26.6587C87.8936 26.7305 88.7896 26.4432 89.4712 25.7981C90.26 25.0811 90.6897 23.8991 90.6897 22.6087V13.1114C90.6897 12.5027 91.1913 12 91.8011 12H94.9186C95.5273 12 96.03 12.5015 96.03 13.1114Z"
                  />
                </svg>
                <div class="preview-hero__actions hide-print">
                  <div
                    class="display-mode-toggle preview-hero__theme-toggle"
                    role="group"
                    :aria-label="$t('travelBook.displayMode')"
                  >
                    <button
                      type="button"
                      class="display-mode-toggle__btn"
                      :class="{ 'is-active': !isDarkMode }"
                      :aria-pressed="!isDarkMode"
                      @click="setDisplayMode('color')"
                    >
                      {{ $t('travelBook.colorMode') }}
                    </button>
                    <button
                      type="button"
                      class="display-mode-toggle__btn"
                      :class="{ 'is-active': isDarkMode }"
                      :aria-pressed="isDarkMode"
                      @click="setDisplayMode('dark')"
                    >
                      {{ $t('travelBook.darkMode') }}
                    </button>
                  </div>
                  <v-button
                    variant="primary"
                    size="sm"
                    class="preview-print-btn text-uppercase x-small fw-bold px-24"
                    @click="printPage"
                  >
                    <span class="mi mi-printer me-8" /> {{ $t('travelBook.printTravelBook') }}
                  </v-button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="featuredImageUrl" class="preview-hero__photo">
          <img :src="featuredImageUrl" :alt="$t('travelBook.featuredImage')" />
        </div>
      </header>

      <!-- Main Layout Table (for PDF/Print consistency) -->
      <main class="bg-white">
        <div class="preview-body py-32 py-md-64">

              <!-- 1. CONTACT RESUME + SUPPORT & CONTACTS -->
              <section class="mb-48 mb-md-64">
                <h3 class="section-title-v1">{{ $t('travelBook.contact') }}</h3>
                <div class="card contact-card p-24 p-md-32">
                  <div class="row align-items-center">
                    <div class="col-md-8">
                      <div class="fw-bold text-uppercase small letter-spacing-1 mb-8 text-brand-muted">{{ $t('travelBook.preparedFor') }}</div>
                      <h4 class="h5 mb-4">{{ caseData.client?.name }} {{ caseData.client?.surname }}</h4>
                      <div class="text-muted small mb-0">{{ caseData.client?.email }}</div>
                    </div>
                    <div class="col-md-4 text-md-end mt-24 mt-md-0">
                      <div class="text-muted x-small text-uppercase letter-spacing-1 mb-4">{{ $t('travelBook.reference') }}</div>
                      <div class="fw-bold text-brand-pink">#BSQ{{ caseData.number }}</div>
                    </div>
                  </div>

                  <template v-if="caseData.contact_text">
                    <hr class="my-24" />
                    <div class="prose small text-muted mb-16" v-html="getSupportPreview()"></div>
                    <v-button
                      v-if="visibleTextLength(caseData.contact_text) > 1600"
                      variant="link"
                      size="sm"
                      class="p-0 text-brand-pink fw-bold text-uppercase x-small letter-spacing-1 text-decoration-none"
                      @click="toggleSupportExpanded"
                    >
                      {{ isSupportExpanded ? $t('travelBook.viewLess') : $t('travelBook.viewMore') }}
                    </v-button>
                  </template>
                </div>
              </section>

              <!-- 2. NEED TO KNOW (highlights first so clients do not miss them) -->
              <section v-if="hasNeedToKnow" class="mb-48 mb-md-64 break-inside-avoid">
                <h3 class="section-title-v1">{{ $t('travelBook.needToKnow') }}</h3>

                <div
                  v-if="hasBeforeTrip"
                  id="section-before"
                  class="highlight-card highlight-card--before mb-20 break-inside-avoid"
                >
                  <div class="highlight-card__header">
                    <span class="mi mi-calandar highlight-card__icon" />
                    <h4 class="highlight-card__title">{{ $t('travelBook.beforeTrip') }}</h4>
                  </div>
                  <div class="highlight-card__body">
                    <div class="prose text-dark mb-16" v-html="getBeforeTripPreview()"></div>
                    <v-button
                      v-if="visibleTextLength(caseData.before_trip_information.description) > 1600"
                      variant="link"
                      size="sm"
                      class="p-0 text-brand-pink fw-bold text-uppercase x-small letter-spacing-1 text-decoration-none"
                      @click="toggleBeforeTripExpanded"
                    >
                      {{ isBeforeTripExpanded ? $t('travelBook.viewLess') : $t('travelBook.viewMore') }}
                    </v-button>
                  </div>
                </div>

                <div
                  v-if="hasLuggage"
                  id="section-luggage"
                  class="highlight-card highlight-card--luggage mb-20 break-inside-avoid"
                >
                  <div class="highlight-card__header">
                    <span class="mi mi-luggage highlight-card__icon" />
                    <h4 class="highlight-card__title">{{ $t('travelBook.luggage') }}</h4>
                  </div>
                  <div class="highlight-card__body">
                    <div class="prose text-dark mb-16" v-html="getLuggagePreview()"></div>
                    <v-button
                      v-if="visibleTextLength(caseData.luggage_information.description) > 1600"
                      variant="link"
                      size="sm"
                      class="p-0 text-brand-pink fw-bold text-uppercase x-small letter-spacing-1 text-decoration-none"
                      @click="toggleLuggageExpanded"
                    >
                      {{ isLuggageExpanded ? $t('travelBook.viewLess') : $t('travelBook.viewMore') }}
                    </v-button>
                  </div>
                </div>

                <div
                  v-if="hasOptional"
                  id="section-optional"
                  class="highlight-card highlight-card--optional mb-20 break-inside-avoid"
                >
                  <div class="highlight-card__header">
                    <span class="mi mi-plus highlight-card__icon" />
                    <h4 class="highlight-card__title">{{ $t('travelBook.optionalExtras') }}</h4>
                  </div>
                  <div class="highlight-card__body">
                    <div class="prose small text-dark" v-html="getOptionalPreview()"></div>
                  </div>
                </div>

                <div
                  v-if="hasAfterTrip"
                  id="section-after"
                  class="highlight-card highlight-card--after break-inside-avoid"
                >
                  <div class="highlight-card__header">
                    <span class="mi mi-calandar highlight-card__icon" />
                    <h4 class="highlight-card__title">{{ $t('travelBook.afterTrip') }}</h4>
                  </div>
                  <div class="highlight-card__body">
                    <div class="prose text-dark mb-16" v-html="getAfterTripPreview()"></div>
                    <v-button
                      v-if="visibleTextLength(caseData.after_trip_information.description) > 1600"
                      variant="link"
                      size="sm"
                      class="p-0 text-brand-pink fw-bold text-uppercase x-small letter-spacing-1 text-decoration-none"
                      @click="toggleAfterTripExpanded"
                    >
                      {{ isAfterTripExpanded ? $t('travelBook.viewLess') : $t('travelBook.viewMore') }}
                    </v-button>
                  </div>
                </div>
              </section>

              <!-- 3. TOUR RESUME (days + jump links) -->
              <section class="mb-48 mb-md-64 break-inside-avoid">
                <h3 class="section-title-v1">{{ $t('travelBook.tourResume') }}</h3>

                <div v-if="hasNeedToKnow" class="toc-links">
                  <button
                    v-if="hasBeforeTrip"
                    type="button"
                    class="toc-link"
                    @click="scrollToSection('before')"
                  >
                    <span class="mi mi-calandar toc-link__icon" />
                    {{ $t('travelBook.beforeTrip') }}
                  </button>
                  <button
                    v-if="hasLuggage"
                    type="button"
                    class="toc-link"
                    @click="scrollToSection('luggage')"
                  >
                    <span class="mi mi-luggage toc-link__icon" />
                    {{ $t('travelBook.luggage') }}
                  </button>
                  <button
                    v-if="hasOptional"
                    type="button"
                    class="toc-link"
                    @click="scrollToSection('optional')"
                  >
                    <span class="mi mi-plus toc-link__icon" />
                    {{ $t('travelBook.optionalExtras') }}
                  </button>
                  <button
                    v-if="hasAfterTrip"
                    type="button"
                    class="toc-link"
                    @click="scrollToSection('after')"
                  >
                    <span class="mi mi-calandar toc-link__icon" />
                    {{ $t('travelBook.afterTrip') }}
                  </button>
                </div>

                <ol v-if="caseData.caseDays?.length" class="toc-days">
                  <li
                    v-for="(day, index) in caseData.caseDays"
                    :key="day.id"
                    class="toc-day"
                  >
                    <button
                      type="button"
                      class="toc-day__btn"
                      @click="openAndScrollToDay(day.id)"
                    >
                      <span class="toc-day__num">{{ dayNumber(index) }}</span>
                      <div class="toc-day__content">
                        <span class="toc-day__label">{{ $t('travelBook.day') }} {{ dayNumber(index) }}</span>
                        <p class="toc-day__route">
                          <template v-if="isSameDayLocation(day)">
                            <span>{{ day.origin?.name || day.destination?.name }}</span>
                          </template>
                          <template v-else-if="day.origin?.name || day.destination?.name">
                            <span v-if="day.origin?.name">{{ day.origin.name }}</span>
                            <span
                              v-if="day.origin?.name && day.destination?.name"
                              class="toc-day__arrow mi mi-arrow-chevron-forward"
                              aria-hidden="true"
                            />
                            <span v-if="day.destination?.name">{{ day.destination.name }}</span>
                          </template>
                          <span v-else>{{ $t('travelBook.day') }} {{ dayNumber(index) }}</span>
                        </p>
                        <div class="toc-day__meta">
                          <span v-if="day.date_formatted || day.date">
                            {{ formatDate(day.date_formatted || day.date, 'dd LLL yyyy') }}
                          </span>
                          <span v-if="day.stage_length || day.stageLength">
                            {{ day.stage_length || day.stageLength }} {{ $t('travelBook.km') }}
                          </span>
                        </div>
                      </div>
                      <span class="toc-day__chevron mi mi-arrow-chevron-forward" aria-hidden="true" />
                    </button>
                  </li>
                </ol>
              </section>

              <!-- 5. JOURNEY MAP -->
              <section v-if="itineraryLocations.length" class="mb-48 mb-md-64 break-inside-avoid">
                <h3 class="section-title-v1">{{ $t('travelBook.map') }}</h3>
                <div class="map-container-v3">
                  <v-map :items="itineraryLocations" :zoom="6" :draw-path="false" />
                </div>
              </section>

              <!-- 6. DETAILED ITINERARY -->
               <section class="mb-64 mb-md-80">
                 <h3 class="section-title-v1">{{ $t('travelBook.tour') }}</h3>

                 <div v-for="(day, index) in caseData.caseDays" :key="day.id" :id="`day-${day.id}`" class="day-accordion mb-24 border">
                  <!-- Day Header (Accordion Toggle) -->
                  <div
                    class="day-accordion-header d-flex justify-content-between align-items-start align-items-md-center p-16 p-md-24 cursor-pointer bg-light-soft"
                    :class="{ 'border-bottom-0': !isDayOpen(day.id) }"
                    @click="toggleDay(day.id)"
                  >
                    <div class="day-accordion-header__main d-flex flex-column flex-md-row align-items-start align-items-md-center gap-8 gap-md-24 flex-grow-1">
                      <div class="text-uppercase fw-bold day-label">{{ $t('travelBook.day') }} {{ dayNumber(index) }}</div>
                      <h4 class="h6 fw-bold mb-0 day-accordion-header__route">
                        <template v-if="isSameDayLocation(day)">
                          <span>{{ day.origin?.name || day.destination?.name }}</span>
                        </template>
                        <template v-else>
                          <span v-if="day.origin?.name">{{ day.origin.name }}</span>
                          <span v-if="day.origin?.name && day.destination?.name" class="mi mi-arrow-chevron-forward mx-4 opacity-40 text-xs" />
                          <span v-if="day.destination?.name">{{ day.destination.name }}</span>
                        </template>
                      </h4>
                      <div v-if="day.date_formatted || day.date" class="day-accordion-header__meta">
                        {{ formatDate(day.date_formatted || day.date, 'dd/MM/yyyy') }}
                      </div>
                      <div v-if="day.stageLength" class="stage-length day-accordion-header__meta">
                        {{ day.stageLength }} {{ $t('travelBook.km') }}
                      </div>
                    </div>
                    <div class="d-flex align-items-center gap-16">
                      <span class="mi" :class="isDayOpen(day.id) ? 'mi-arrow-chevron-up' : 'mi-arrow-chevron-down'"></span>
                    </div>
                  </div>

                  <!-- Accordion Body -->
                  <div v-if="isDayOpen(day.id)" class="day-accordion-body p-16 p-md-40 bg-white">
                    <!-- Day Items (Services) - Grouped -->
                    <div class="day-services">
                      <template v-for="group in getGroupedDayItems(day.caseDayItems)" :key="`${group.type}-${group.id}`">
                        <div class="service-item mb-40 mb-md-64 break-inside-avoid">
                          <div class="row g-24 g-md-40">
                            <!-- Image Carousel (Shared for the group) -->
                            <div v-if="getItemImages(group.items[0]).length > 0" class="col-12 mb-24">
                              <div class="image-carousel-wrapper">
                                <div class="carousel-container border position-relative" :style="{ aspectRatio: '16/9' }">
                                  <img
                                    :src="getItemImages(group.items[0])[carouselIndex(group.id)]"
                                    class="w-100 h-100"
                                    style="object-fit: contain;"
                                    :alt="getItemTitle(group.items[0])"
                                  />
                                  <!-- Carousel Controls -->
                                  <div v-if="getItemImages(group.items[0]).length > 1" class="carousel-controls position-absolute bottom-0 end-0">
                                    <button
                                      class="carousel-btn carousel-prev"
                                      @click.stop="prevCarouselImage(group.id)"
                                    >
                                      <span class="mi mi-arrow-chevron-back"></span>
                                    </button>
                                    <span class="carousel-counter">{{ carouselIndex(group.id) + 1 }}/{{ getItemImages(group.items[0]).length }}</span>
                                    <button
                                      class="carousel-btn carousel-next"
                                      @click.stop="nextCarouselImage(group.id)"
                                    >
                                      <span class="mi mi-arrow-chevron-forward"></span>
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>

                            <div class="col-12">
                              <div class="d-flex align-items-center gap-12 mb-12">
                                <div class="service-type-badge text-uppercase x-small fw-bold px-8 py-2">
                                  {{ getItemTypeLabel(group.type) }}
                                </div>
                                <span v-if="group.items.length > 1" class="badge service-count-badge x-small px-8 text-uppercase">
                                  {{ group.items.length }} {{ group.items.length === 1 ? $t('travelBook.service') : $t('travelBook.services') }}
                                </span>
                              </div>

                              <h5 class="h6 fw-bold mb-16">
                                <span v-if="group.items.length > 1" class="text-muted fw-normal me-8">{{ group.items.length }}x</span>
                                {{ getItemTitle(group.items[0]) }}
                              </h5>

                              <!-- Shared Item Description (only from first item in group) -->
                              <div v-if="group.items[0].item_description" class="service-desc-wrapper mb-24">
                                <div
                                  class="service-desc prose small mb-12 text-muted"
                                  v-html="
                                    isExpanded(group.id) || visibleTextLength(group.items[0].item_description) <= 1600
                                      ? group.items[0].item_description
                                      : truncateDescription(group.items[0].item_description)
                                  "
                                ></div>

                                <v-button
                                  v-if="visibleTextLength(group.items[0].item_description) > 1600"
                                  variant="link"
                                  size="sm"
                                  class="p-0 text-brand-pink fw-bold text-uppercase x-small letter-spacing-1 text-decoration-none"
                                  @click="toggleExpand(group.id)"
                                >
                                  {{ isExpanded(group.id) ? $t('travelBook.viewLess') : $t('travelBook.viewMore') }}
                                </v-button>
                              </div>

                              <!-- Consolidated Resource Details -->
                              <div v-if="group.groupedResources?.length" class="service-resources">
                                <div v-for="res in group.groupedResources" :key="res.id" class="resource-row p-16 border bg-light-soft mb-12">
                                  <div class="d-flex justify-content-between align-items-center mb-12">
                                    <div class="fw-bold small text-uppercase letter-spacing-1">
                                      <span v-if="res.count > 1" class="text-muted fw-normal me-8">{{ res.count }}x</span>
                                      {{ res.resource_name }}
                                    </div>
                                    <span v-if="nightsCount(res) > 0 && group.type === 'hotel'" class="badge service-count-badge x-small px-8">
                                      {{ $t('travelBook.quantityNights', nightsCount(res)) }}
                                    </span>
                                  </div>
                                  <div
                                    v-if="group.type === 'hotel'"
                                    class="d-flex flex-wrap gap-12 gap-md-16 x-small text-muted mb-16"
                                  >
                                    <span v-if="res.checkin_date_formatted || res.checkin_date">
                                      <b>{{ $t('general.shared.checkIn') }}:</b>
                                      {{ formatDate(res.checkin_date_formatted || res.checkin_date, 'dd MMM') }}
                                    </span>
                                    <span v-if="res.checkout_date_formatted || res.checkout_date">
                                      <b>{{ $t('general.shared.checkOut') }}:</b>
                                      {{ formatDate(res.checkout_date_formatted || res.checkout_date, 'dd MMM') }}
                                    </span>
                                  </div>

                                  <div
                                    v-if="(group.type === 'transport' || group.type === 'transfer') && (resourceOrigin(res, day) || resourceDestination(res, day))"
                                    class="d-flex flex-wrap gap-12 gap-md-16 x-small text-muted mb-16"
                                  >
                                    <template v-if="isSameResourceRoute(res, day)">
                                      <span>{{ resourceOrigin(res, day) || resourceDestination(res, day) }}</span>
                                    </template>
                                    <template v-else>
                                      <span v-if="resourceOrigin(res, day)">
                                        <b>{{ $t('general.shared.origin') }}:</b> {{ resourceOrigin(res, day) }}
                                      </span>
                                      <span v-if="resourceDestination(res, day)">
                                        <b>{{ $t('general.shared.destination') }}:</b> {{ resourceDestination(res, day) }}
                                      </span>
                                    </template>
                                  </div>

                                  <!-- Amenities -->
                                  <div v-if="res.amenities?.length" class="d-flex flex-wrap gap-8 mb-16">
                                    <span
                                      v-for="amenity in res.amenities"
                                      :key="amenity"
                                      class="x-small border bg-white rounded-pill px-12 py-4 text-muted text-uppercase fw-bold letter-spacing-1"
                                      style="font-size: 10px;"
                                    >
                                      {{ amenity.replace('_', ' ') }}
                                    </span>
                                  </div>

                                  <!-- Client Observations -->
                                  <div v-if="res.clientObservations" class="small text-muted mb-16">
                                    <b>{{ $t('general.shared.client_observations') }}:</b>
                                    <p></p>
                                    <span v-html="res.clientObservations" />
                                  </div>

                                  <div v-if="res.supplier_info" class="supplier-links d-flex gap-16 border-top pt-16">
                                    <a
                                      v-if="res.supplier_info.map_url"
                                      :href="res.supplier_info.map_url"
                                      target="_blank"
                                      class="x-small text-brand-pink fw-bold text-decoration-none d-flex align-items-center gap-4"
                                    >
                                      <span class="mi mi-map text-xs" /> {{ $t('travelBook.map') }}
                                    </a>
                                    <a
                                      v-if="res.supplier_info.phone"
                                      :href="`tel:${res.supplier_info.phone}`"
                                      class="x-small text-brand-pink fw-bold text-decoration-none d-flex align-items-center gap-4"
                                    >
                                      <span class="mi mi-call text-xs" /> {{ res.supplier_info.phone }}
                                    </a>
                                    <a
                                      v-if="res.supplier_info.site_url"
                                      :href="res.supplier_info.site_url"
                                      target="_blank"
                                      class="x-small text-brand-pink fw-bold text-decoration-none d-flex align-items-center gap-4"
                                    >
                                      <span class="mi mi-world-net text-xs" /> {{ $t('travelBook.website') }}
                                    </a>
                                  </div>
                                </div>
                              </div>
                              <!-- Grouped Notes -->
                              <div v-if="group.allNotes?.length" class="service-notes-container mt-20">
                                <div
                                  v-for="(note, nIdx) in group.allNotes"
                                  :key="nIdx"
                                  class="service-notes border-start border-3 ps-20 py-4 mb-12 small text-muted italic"
                                  style="white-space: pre-wrap;"
                                >
                                  {{ stripHtml(note) }}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </template>

                      <!-- Location Guide Information (What to Do) - Expandable -->
                      <div v-for="locInfo in getDayLocationInfo(day)" :key="locInfo.id" class="location-info-block mt-32 border break-inside-avoid">
                        <div
                          class="location-guide-header p-16 p-md-24 bg-light-soft cursor-pointer d-flex justify-content-between align-items-center"
                          :class="{ 'border-bottom-0': !isLocationGuideOpen(locInfo.id) }"
                          @click="toggleLocationGuide(locInfo.id)"
                        >
                          <div class="d-flex align-items-center gap-12">
                            <span class="mi mi-camera opacity-40 text-xs" />
                            <h5 class="location-guide-header__title mb-0 fw-bold">{{ $t('travelBook.whatToDoLocation', { location: locInfo.locationName }) }}</h5>
                          </div>
                          <div class="mi" :class="isLocationGuideOpen(locInfo.id) ? 'mi-arrow-chevron-up' : 'mi-arrow-chevron-down'"></div>
                        </div>

                        <div v-if="isLocationGuideOpen(locInfo.id)" class="p-16 p-md-40 bg-white">
                          <div class="prose small text-muted mb-24" v-html="getLocationGuidePreview(locInfo)"></div>

                          <v-button
                            v-if="locInfo.description && visibleTextLength(locInfo.description) > 1600"
                            variant="link"
                            size="sm"
                            class="p-0 text-brand-pink fw-bold text-uppercase x-small letter-spacing-1 text-decoration-none"
                            @click="toggleLocationGuideExpand(locInfo.id)"
                          >
                            {{ isLocationGuideExpanded(locInfo.id) ? $t('travelBook.viewLess') : $t('travelBook.viewMore') }}
                          </v-button>

                          <!-- Location Media Carousel -->
                          <div v-if="locInfo.media?.length" class="mt-24">
                            <div class="location-media-carousel position-relative" style="aspect-ratio: 16/9; max-height: 550px;">
                              <img
                                :src="locInfo.media[locationCarouselIndex(locInfo.id)]?.original_url || locInfo.media[locationCarouselIndex(locInfo.id)]?.storageUrl"
                                class="w-100 h-100 border"
                                style="object-fit: contain;"
                              />
                              <!-- Media Controls -->
                              <div v-if="locInfo.media.length > 1" class="carousel-controls position-absolute bottom-0 end-0">
                                <button
                                  class="carousel-btn carousel-prev"
                                  @click.stop="prevLocationCarouselImage(locInfo.id)"
                                >
                                  <span class="mi mi-arrow-chevron-back"></span>
                                </button>
                                <span class="carousel-counter">{{ locationCarouselIndex(locInfo.id) + 1 }}/{{ locInfo.media.length }}</span>
                                <button
                                  class="carousel-btn carousel-next"
                                  @click.stop="nextLocationCarouselImage(locInfo.id)"
                                >
                                  <span class="mi mi-arrow-chevron-forward"></span>
                                </button>
                              </div>
                            </div>
                          </div>

                          <div class="mt-24 d-flex gap-16">
                            <a v-if="locInfo.mapUrl" :href="locInfo.mapUrl" target="_blank" class="x-small text-brand-pink fw-bold text-decoration-none d-flex align-items-center gap-4">
                              <span class="mi mi-location text-xs" /> {{ $t('travelBook.viewGoogleMaps') }}
                            </a>
                            <a v-if="locInfo.siteUrl" :href="locInfo.siteUrl" target="_blank" class="x-small text-brand-pink fw-bold text-decoration-none d-flex align-items-center gap-4">
                              <span class="mi mi-world-net text-xs" /> {{ $t('travelBook.viewWebsite') }}
                            </a>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              <!-- 7. CTA -->
              <section v-if="caseData.is_draft" class="mb-48 mb-md-64 break-inside-avoid">
                <div class="cta-banner-v1 text-white p-24 p-md-64 text-center">
                  <h2 class="cta-banner-v1__title h2 fw-extra-bold mb-16 mb-md-20 text-white">
                    {{ $t('travelBook.readyToAdventure') }}</h2>
                  <p class="cta-banner-v1__text opacity-70 mb-24 mb-md-48">{{ $t('travelBook.finalizeReservation') }}</p>
                  <div class="cta-banner-v1__actions d-flex flex-wrap justify-content-center gap-12 gap-md-24">
                    <v-button variant="primary" size="lg"
                    class="cta-btn cta-btn--primary fw-bold px-24 py-12 px-md-40 py-md-16 text-uppercase shadow-sm" @click="contactWhatsApp">
                      {{ $t('travelBook.whatsappSpecialist') }}
                    </v-button>
                    <v-button variant="outline-light" size="lg" class="cta-btn cta-btn--outline fw-bold px-24 py-12 px-md-40 py-md-16 text-uppercase" @click="bookNow">
                      {{ $t('travelBook.requestReservation') }}
                    </v-button>
                  </div>
                </div>
              </section>

        </div>
      </main>

      <!-- 8. FOOTER -->
      <footer class="footer-v1 text-white py-48">
        <div class="preview-body">
          <div class="row align-items-center">
            <div class="col-md-8 text-center text-md-start mb-24 mb-md-0">
              <div class="footer-v1__links d-flex justify-content-center justify-content-md-start gap-16 gap-md-24 mb-16 text-uppercase fw-bold">
                <a href="https://www.instagram.com/waw.travel/" target="_blank" rel="noopener noreferrer" class="text-white text-decoration-none">Instagram</a>
                <a href="https://www.facebook.com/WAW.travel.eu" target="_blank" rel="noopener noreferrer" class="text-white text-decoration-none">Facebook</a>
                <a href="https://www.linkedin.com/company/waw-travel/" target="_blank" rel="noopener noreferrer" class="text-white text-decoration-none">LinkedIn</a>
              </div>
              <p class="text-xs opacity-30 mb-0">&copy; {{ new Date().getFullYear() }} WAW Travels. All rights reserved.</p>
              <p class="text-xs fw-bold letter-spacing-2 mt-8 opacity-50">SITE.WAW.TRAVEL</p>
            </div>
            <div class="col-md-4 text-center text-md-end">
              <img src="https://site.waw.travel/images/wawlogo.svg" alt="WAW" class="invert-filter" style="height: 24px; width: auto;" />
            </div>
          </div>
        </div>
      </footer>

      <!-- Back to Top -->
      <div v-if="showScrollTop" class="back-to-top-v1 hide-print shadow-lg" @click="scrollToTop">
        <span class="mi mi-arrow-chevron-up" />
      </div>
    </template>

    <div v-else class="preview-empty vh-100 d-flex flex-column align-items-center justify-content-center text-center p-32">
      <img src="https://site.waw.travel/images/wawlogo.svg" alt="WAW" height="50" class="mb-48 opacity-10" />
      <h2 class="fw-bold text-uppercase letter-spacing-3 small text-muted mb-16">{{ $t('travelBook.itineraryNotFound') }}</h2>
      <p class="text-muted max-w-400 mb-32 small">{{ $t('travelBook.itineraryNotFoundDescription') }}</p>
      <v-button variant="primary" class="empty-state-btn px-32 py-12 text-uppercase x-small fw-bold letter-spacing-2" @click="$router.push('/')">
        {{ $t('travelBook.returnHome') }}
      </v-button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed } from "vue";
import { useRoute } from "vue-router";
import { useI18n } from "vue-i18n";
import api from "@/api";
import VLoader from "@/components/vendor/loader/VLoader.vue";
import VButton from "@/components/vendor/basic/button/VButton.vue";
import VMap from "@/components/vendor/VMap.vue";
import { DateTime } from "luxon";
import { findConceptByKey } from "@/helpers/ConceptHelper";
import { replaceTemplateVariables } from "@/composables/useTemplateVariables";

const route = useRoute();
const { t } = useI18n();
const THEME_STORAGE_KEY = 'waw-travel-book-theme';

const readStoredDarkMode = () => {
  try {
    return localStorage.getItem(THEME_STORAGE_KEY) === 'dark';
  } catch {
    return false;
  }
};

const loading = ref(true);
const isDarkMode = ref(readStoredDarkMode());
const caseData = ref<any>(null);

const setDisplayMode = (mode: 'color' | 'dark') => {
  isDarkMode.value = mode === 'dark';
  try {
    localStorage.setItem(THEME_STORAGE_KEY, mode);
  } catch {
    // Ignore storage errors (private browsing, etc.)
  }
};
const showScrollTop = ref(false);
const openDays = ref<number[]>([]);
const expandedItems = ref<number[]>([]);
const openLocationGuides = ref<number[]>([]);
const expandedLocationGuides = ref<number[]>([]);
const isBeforeTripExpanded = ref(false);
const isAfterTripExpanded = ref(false);
const isLuggageExpanded = ref(false);
const isSupportExpanded = ref(false);
const carouselIndices = ref<Record<number, number>>({});
const locationCarouselIndices = ref<Record<number, number>>({});

const toggleDay = (dayId: number) => {
  const index = openDays.value.indexOf(dayId);
  if (index > -1) {
    openDays.value.splice(index, 1);
  } else {
    openDays.value.push(dayId);
    openDayLocationGuides(dayId);
  }
};

const isDayOpen = (dayId: number) => openDays.value.includes(dayId);

const toggleExpand = (itemId: number) => {
  const index = expandedItems.value.indexOf(itemId);
  if (index > -1) expandedItems.value.splice(index, 1);
  else expandedItems.value.push(itemId);
};

const isExpanded = (itemId: number) => expandedItems.value.includes(itemId);

const scrollToDay = (dayId: number) => {
  setTimeout(() => {
    const el = document.getElementById(`day-${dayId}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, 100);
};

const openDayLocationGuides = (dayId: number) => {
  const day = caseData.value?.caseDays?.find((d: any) => d.id === dayId);
  if (!day) return;

  for (const locInfo of getDayLocationInfo(day)) {
    if (locInfo?.id != null && !openLocationGuides.value.includes(locInfo.id)) {
      openLocationGuides.value.push(locInfo.id);
    }
  }
};

const openAndScrollToDay = (dayId: number) => {
  if (!isDayOpen(dayId)) toggleDay(dayId);
  else openDayLocationGuides(dayId);
  scrollToDay(dayId);
};

const scrollToSection = (sectionId: string) => {
  setTimeout(() => {
    const el = document.getElementById(`section-${sectionId}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, 100);
};

const truncateDescription = (description: string, limit = 1600) => {
  if (!description) return "";
  if (description.length <= limit) return description;

  // Basic truncate that tries not to break HTML tags too badly
  const truncated = description.substring(0, limit);
  return truncated + "...";
};

const stripHtml = (html: string | null | undefined) => {
  if (!html) return '';
  return html
    .replace(/<\/p>\s*<p[^>]*>/gi, '\n\n')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<[^>]+>/g, '')
    .trim();
};

// The raw HTML string length is not a reliable measure for "is there more to read":
// rich text editors often wrap even a short sentence in a lot of markup, which would
// falsely trigger the "View More" button. Measure the visible text instead.
const visibleTextLength = (html: string | null | undefined) => stripHtml(html).length;

// --- Template variable values (client-side fallback replacement) ---
// The backend (PublicController::getPreviewData) already replaces these, but we
// also replace them here so the internal preview works even if the API response
// still contains raw tokens.
const templateVariableValues = computed(() => {
  const days = caseData.value?.caseDays?.length ?? 0;
  let noOfNights = 0;
  let hotelCount = 0;
  let refugeCount = 0;
  for (const day of caseData.value?.caseDays ?? []) {
    for (const item of day.caseDayItems ?? []) {
      for (const res of item.caseDayItemResources ?? []) {
        const supplierType = res.supplierResource?.supplier?.type ?? res.supplier_info?.type;
        if (supplierType === 'hotel' || supplierType === 'refuge') {
          noOfNights += res.nights ? Number(res.nights) : 0;
        }
        if (supplierType === 'hotel') hotelCount++;
        else if (supplierType === 'refuge') refugeCount++;
      }
    }
  }
  return {
    noOfDays: days,
    noOfNights,
    hotelCount,
    refugeCount,
    hotelType: 'hotel',
    refugeType: 'refuge',
  };
});

const formatDate = (date: string, format = "dd LLL yyyy") => {
  if (!date) return "";
  return DateTime.fromISO(date).toFormat(format);
};

const dayNumber = (index: number | string) => {
  const n = Number(index) + 1;
  return n < 10 ? `0${n}` : `${n}`;
};

const nightsCount = (res: any) => {
  const n = Number(res?.nights);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

const normalizeLocationName = (name?: string | null) => (name || '').trim().toLowerCase();

const isSameDayLocation = (day: any) => {
  if (day?.origin?.id && day?.destination?.id) return day.origin.id === day.destination.id;
  const origin = normalizeLocationName(day?.origin?.name);
  const dest = normalizeLocationName(day?.destination?.name);
  return Boolean(origin && dest && origin === dest);
};

const resourceOrigin = (res: any, day: any) => res?.origin_name || day?.origin?.name || '';
const resourceDestination = (res: any, day: any) => res?.destination_name || day?.destination?.name || '';

const isSameResourceRoute = (res: any, day: any) => {
  const origin = normalizeLocationName(resourceOrigin(res, day));
  const dest = normalizeLocationName(resourceDestination(res, day));
  return Boolean(origin && dest && origin === dest);
};

const getTravelTypeLabel = (typeKey: string) => {
  if (!typeKey) return "";
  const concept = findConceptByKey('tour.travel_type', typeKey);
  return (typeof concept === 'object' && concept && 'name' in concept ? concept.name : typeKey.replace('_', ' ').toUpperCase());
};

const getTravelTypeIconClass = (typeKey: string) => {
  const key = (typeKey || "").toLowerCase();
  if (key.includes("bike") || key.includes("cycl")) return "icon icon-bike";
  if (key.includes("walk") || key.includes("hik")) return "icon icon-run";
  return "mi mi-profile";
};

const extractInfoObject = (raw: any, currentLang: string): { description: string; translations?: any[] } | null => {
  if (!raw) return null;

  if (typeof raw === "string") {
    return visibleTextLength(raw) > 0 ? { description: raw } : null;
  }

  if (typeof raw !== "object") return null;

  if (Array.isArray(raw.translations) && raw.translations.length > 0) {
    const match =
      raw.translations.find((t: any) => t.locale === currentLang && visibleTextLength(t.description) > 0)
      || raw.translations.find((t: any) => t.locale === "en" && visibleTextLength(t.description) > 0)
      || raw.translations.find((t: any) => visibleTextLength(t.description) > 0);

    if (match?.description) {
      return { description: match.description, translations: raw.translations };
    }
  }

  if (visibleTextLength(raw.description) > 0) {
    return { description: raw.description, translations: raw.translations };
  }

  const dictDesc =
    raw[currentLang]?.description
    || raw.en?.description
    || raw.nl?.description
    || raw.es?.description
    || (Object.values(raw).find((v: any) => v?.description) as any)?.description;

  if (typeof dictDesc === "string" && visibleTextLength(dictDesc) > 0) {
    return { description: dictDesc };
  }

  return null;
};

const firstInfo = (lang: string, ...candidates: any[]) => {
  for (const candidate of candidates) {
    const extracted = extractInfoObject(candidate, lang);
    if (extracted) return extracted;
  }
  return null;
};

const normalizeCaseData = (data: any) => {
  if (!data) return null;
  const normalized = { ...data };
  const lang = data.client?.preferred_language || 'en';

  // Strip HTML from top-level names
  if (normalized.name) normalized.name = stripHtml(normalized.name);
  if (normalized.tour_name) normalized.tour_name = stripHtml(normalized.tour_name);
  if (normalized.origin?.name) normalized.origin.name = stripHtml(normalized.origin.name);
  if (normalized.destination?.name) normalized.destination.name = stripHtml(normalized.destination.name);
  if (normalized.client?.name) normalized.client.name = stripHtml(normalized.client.name);
  if (normalized.client?.surname) normalized.client.surname = stripHtml(normalized.client.surname);

  // Map snake_case to camelCase for the template
  if (data.case_days && !data.caseDays) normalized.caseDays = data.case_days;
  if (data.pax_count && (!data.paxes || data.paxes.length === 0)) {
    normalized.paxes = Array(data.pax_count).fill({});
  }
  if (data.featured_image && !data.featuredImage) {
    normalized.featuredImage = data.featured_image;
  }
  if (normalized.activity) {
    const activityImage = normalized.activity.featuredImage || normalized.activity.featured_image;
    if (activityImage) {
      normalized.activity = { ...normalized.activity, featuredImage: activityImage };
    }
  }

  if (normalized.caseDays) {
    normalized.caseDays = normalized.caseDays.map((day: any) => {
      const nDay = { ...day };
      if (day.case_day_items && !day.caseDayItems) nDay.caseDayItems = day.case_day_items;
      if (day.stage_length && !day.stageLength) nDay.stageLength = day.stage_length;
      if (nDay.origin?.name) nDay.origin.name = stripHtml(nDay.origin.name);
      if (nDay.destination?.name) nDay.destination.name = stripHtml(nDay.destination.name);
      if (nDay.caseDayItems) {
        nDay.caseDayItems = nDay.caseDayItems.map((item: any) => {
          const nItem = { ...item };
          if (item.item_data && !item.itemData) nItem.itemData = item.item_data;

          // Localize itemData name/description/title for the client language
          if (nItem.itemData) {
            nItem.itemData.name = stripHtml(getLocalizedText(nItem.itemData, 'name', lang) || nItem.itemData.name);
            nItem.itemData.description = getLocalizedText(nItem.itemData, 'description', lang) || nItem.itemData.description;
            nItem.itemData.title = stripHtml(getLocalizedText(nItem.itemData, 'title', lang) || nItem.itemData.title);
            if (nItem.itemData.description) {
              nItem.item_description = nItem.itemData.description;
            }
          }

          if (item.case_day_item_resources && !item.caseDayItemResources) {
            nItem.caseDayItemResources = item.case_day_item_resources.map((res: any) => {
              const nRes = { ...res };
              const sRes = res.supplier_resource || res.supplierResource;
              if (sRes) {
                nRes.supplierResource = sRes;
                nRes.resource_name = stripHtml(getLocalizedText(sRes, 'name', lang) || sRes.name);
                const supplierFromApi = res.supplier_info ? { ...res.supplier_info, name: stripHtml(res.supplier_info.name) } : null;
                if (sRes.supplier) {
                  const supplierName = stripHtml(getLocalizedText(sRes.supplier, 'name', lang) || sRes.supplier.name);
                  nRes.supplier_info = { ...sRes.supplier, name: supplierName };
                } else if (supplierFromApi) {
                  nRes.supplier_info = supplierFromApi;
                } else {
                  nRes.supplier_info = null;
                }
                if (sRes.amenities) {
                  try {
                    nRes.amenities = typeof sRes.amenities === 'string' ? JSON.parse(sRes.amenities) : sRes.amenities;
                  } catch (e) {
                    nRes.amenities = [];
                  }
                }
              }
              return nRes;
            });
          }
          return nItem;
        });

        // Try to find stageLength if missing from day object
        if (!nDay.stageLength) {
          const stageInfo = nDay.caseDayItems.find((i: any) => i.type === 'stage_information');
          if (stageInfo?.itemData?.stage_length) {
            nDay.stageLength = stageInfo.itemData.stage_length;
          }
        }
      }
      return nDay;
    });
  }

  // Resolve highlight sections from case data, then activity fallback.
  // Descriptions live in translations; never keep an empty preferred-locale overwrite.
  const activity = data.activity || {};
  normalized.before_trip_information = firstInfo(
    lang,
    data.before_trip_information,
    data.beforeTripInformation,
    activity.before_information,
    activity.beforeInformation
  );
  normalized.luggage_information = firstInfo(
    lang,
    data.luggage_information,
    data.luggageInformation,
    activity.luggage_information,
    activity.luggageInformation
  );
  normalized.after_trip_information = firstInfo(
    lang,
    data.after_trip_information,
    data.afterTripInformation,
    activity.after_information,
    activity.afterInformation
  );
  normalized.included_trip_information = firstInfo(
    lang,
    data.included_trip_information,
    data.includedTripInformation,
    activity.included_information,
    activity.includedInformation
  );
  normalized.excluded_trip_information = firstInfo(
    lang,
    data.excluded_trip_information,
    data.excludedTripInformation,
    activity.excluded_information,
    activity.excludedInformation
  );
  normalized.optional_trip_information = firstInfo(
    lang,
    data.optional_trip_information,
    data.optionalTripInformation,
    activity.extras_information,
    activity.extrasInformation,
    activity.optional_information,
    activity.optionalInformation
  );

  return normalized;
};

const getLocalizedText = (obj: any, field: string, locale: string): string | null => {
  if (!obj || !Array.isArray(obj.translations)) return null;
  const translation = obj.translations.find((tr: any) => tr.locale === locale && (!tr.field || tr.field === field));
  return translation && field in translation ? translation[field] : null;
};

const getItemTypeLabel = (type: string) => {
  const label = t(`travelBook.${type}`);
  return label !== `travelBook.${type}` ? label : type.replace('_', ' ').toUpperCase();
};

const getItemTitle = (item: any) => {
  const data = item.itemData || item.item_data;
  let title = "";
  if (item.type === "stage_information" || item.type === "general_information") {
    title = data?.title || item.type.replace("_", " ").toUpperCase();
  } else if (item.type === "hotel") {
    const resources = item.caseDayItemResources || item.case_day_item_resources || [];
    const firstRes = resources[0];
    const supplierName = firstRes?.supplier_info?.name
      || firstRes?.supplierResource?.supplier?.name
      || data?.supplier?.name
      || null;
    if (supplierName) {
      title = supplierName;
    } else {
      // Resource title is the actual hotel name; SupplierResource name may be a room type/description.
      const candidate = data?.title || data?.name || firstRes?.resource_name;
      if (candidate && !/<[^>]+>/.test(candidate) && candidate.length < 80) {
        title = candidate;
      } else {
        title = t('travelBook.hotel');
      }
    }
  } else {
    title = data?.name || item.type.replace("_", " ").toUpperCase();
  }
  return stripHtml(title);
};

const getDayLocationInfo = (day: any) => {
  const infos: any[] = [];
  // What-to-do is shown only for the destination of the day (latter stage).
  // The origin city is already covered by the previous day.
  if (day.destination?.resources?.length) {
    day.destination.resources.filter((r: any) => r.type === 'information').forEach((r: any) => {
      infos.push({ ...r, locationName: day.destination.name });
    });
  }
  return infos;
};

// Service Grouping - Group items by type and ID to avoid repetition
const getGroupedDayItems = (items: any[]) => {
  const groups: Record<string, any> = {};
  items.forEach(item => {
    const data = item.itemData || item.item_data;
    // Only group if it's a "service" product with an ID.
    // Types like 'general_information' or 'stage_information' stay unique.
    const isGroupable = item.type !== 'general_information' &&
                       item.type !== 'stage_information' &&
                       data?.id;

    const groupKey = isGroupable ? `${item.type}-${data.id}` : `unique-${item.id}`;

    if (!groups[groupKey]) {
      groups[groupKey] = {
        type: item.type,
        items: [],
        itemData: data,
        id: item.id // Use first item id as base for carousel/expand state
      };
    }
    groups[groupKey].items.push(item);
  });

  return Object.values(groups).map((group: any) => {
    // Consolidation: Group identical resources within this service group
    const resources: any[] = [];
    group.items.forEach((item: any) => {
      if (item.caseDayItemResources) {
        resources.push(...item.caseDayItemResources);
      }
    });

    const groupedResources: Record<string, any> = {};
    resources.forEach(res => {
      // Create a key based on name and nights/dates to group identical rooms/items
      const resKey = `${res.resource_name}-${res.nights}-${res.checkin_date}-${res.checkout_date}`;
      if (!groupedResources[resKey]) {
        groupedResources[resKey] = { ...res, count: 1 };
      } else {
        groupedResources[resKey].count++;
      }
    });
    group.groupedResources = Object.values(groupedResources);

    // Collect all unique notes
    const notesSet = new Set();
    group.items.forEach((i: any) => {
      if (i.notes) notesSet.add(i.notes);
    });
    group.allNotes = Array.from(notesSet);

    return group;
  });
};

// Get all images from an item
const getItemImages = (item: any) => {
  const data = item.itemData || item.item_data;
  if (data?.media && data.media.length > 0) {
    return data.media.map((m: any) => m.original_url || m.preview_url || m.storageUrl || m.storage_url);
  }
  return [];
};

// Carousel management for services
const carouselIndex = (itemId: number) => carouselIndices.value[itemId] || 0;
const nextCarouselImage = (itemId: number) => {
  const item = caseData.value?.caseDays?.flatMap((d: any) => d.caseDayItems).find((i: any) => i.id === itemId);
  if (item) {
    const images = getItemImages(item);
    const current = carouselIndex(itemId);
    carouselIndices.value[itemId] = (current + 1) % images.length;
  }
};
const prevCarouselImage = (itemId: number) => {
  const item = caseData.value?.caseDays?.flatMap((d: any) => d.caseDayItems).find((i: any) => i.id === itemId);
  if (item) {
    const images = getItemImages(item);
    const current = carouselIndex(itemId);
    carouselIndices.value[itemId] = (current - 1 + images.length) % images.length;
  }
};

// Carousel management for location guides
const locationCarouselIndex = (locId: number) => locationCarouselIndices.value[locId] || 0;
const nextLocationCarouselImage = (locId: number) => {
  const day = caseData.value?.caseDays?.find((d: any) =>
    getDayLocationInfo(d).some((l: any) => l.id === locId)
  );
  if (day) {
    const locInfo = getDayLocationInfo(day).find((l: any) => l.id === locId);
    if (locInfo?.media) {
      const current = locationCarouselIndex(locId);
      locationCarouselIndices.value[locId] = (current + 1) % locInfo.media.length;
    }
  }
};
const prevLocationCarouselImage = (locId: number) => {
  const day = caseData.value?.caseDays?.find((d: any) =>
    getDayLocationInfo(d).some((l: any) => l.id === locId)
  );
  if (day) {
    const locInfo = getDayLocationInfo(day).find((l: any) => l.id === locId);
    if (locInfo?.media) {
      const current = locationCarouselIndex(locId);
      locationCarouselIndices.value[locId] = (current - 1 + locInfo.media.length) % locInfo.media.length;
    }
  }
};

// Location Guide expand/collapse
const toggleLocationGuide = (locId: number) => {
  const index = openLocationGuides.value.indexOf(locId);
  if (index > -1) openLocationGuides.value.splice(index, 1);
  else openLocationGuides.value.push(locId);
};
const isLocationGuideOpen = (locId: number) => openLocationGuides.value.includes(locId);

const toggleLocationGuideExpand = (locId: number) => {
  const index = expandedLocationGuides.value.indexOf(locId);
  if (index > -1) expandedLocationGuides.value.splice(index, 1);
  else expandedLocationGuides.value.push(locId);
};
const isLocationGuideExpanded = (locId: number) => expandedLocationGuides.value.includes(locId);

const getLocationGuidePreview = (locInfo: any) => {
  if (!locInfo.description) return "";
  const isExpanded = isLocationGuideExpanded(locInfo.id);
  if (isExpanded || visibleTextLength(locInfo.description) <= 1600) return locInfo.description;
  return truncateDescription(locInfo.description);
};

const getBeforeTripPreview = () => {
  if (!caseData.value?.before_trip_information?.description) return "";
  const desc = replaceTemplateVariables(caseData.value.before_trip_information.description, templateVariableValues.value);
  if (isBeforeTripExpanded.value || visibleTextLength(desc) <= 1600) return desc;
  return truncateDescription(desc);
};

const getLuggagePreview = () => {
  if (!caseData.value?.luggage_information?.description) return "";
  const desc = replaceTemplateVariables(caseData.value.luggage_information.description, templateVariableValues.value);
  if (isLuggageExpanded.value || visibleTextLength(desc) <= 1600) return desc;
  return truncateDescription(desc);
};

const getAfterTripPreview = () => {
  if (!caseData.value?.after_trip_information?.description) return "";
  const desc = replaceTemplateVariables(caseData.value.after_trip_information.description, templateVariableValues.value);
  if (isAfterTripExpanded.value || visibleTextLength(desc) <= 1600) return desc;
  return truncateDescription(desc);
};

const getOptionalPreview = () => {
  if (!caseData.value?.optional_trip_information?.description) return "";
  return replaceTemplateVariables(caseData.value.optional_trip_information.description, templateVariableValues.value);
};

const getSupportPreview = () => {
  if (!caseData.value?.contact_text) return "";
  const desc = replaceTemplateVariables(caseData.value.contact_text, templateVariableValues.value);
  if (isSupportExpanded.value || visibleTextLength(desc) <= 1600) return desc;
  return truncateDescription(desc);
};

const toggleBeforeTripExpanded = () => {
  isBeforeTripExpanded.value = !isBeforeTripExpanded.value;
};

const toggleLuggageExpanded = () => {
  isLuggageExpanded.value = !isLuggageExpanded.value;
};

const toggleAfterTripExpanded = () => {
  isAfterTripExpanded.value = !isAfterTripExpanded.value;
};

const toggleSupportExpanded = () => {
  isSupportExpanded.value = !isSupportExpanded.value;
};

const itineraryLocations = computed(() => {
  if (!caseData.value?.caseDays) return [];

  const locations: any[] = [];
  const addedIds = new Set();

  caseData.value.caseDays.forEach((day: any) => {
    if (day.origin && !addedIds.has(day.origin.id)) {
      locations.push({
        id: day.origin.id,
        name: day.origin.name,
        lat: parseFloat(day.origin.lat),
        lng: parseFloat(day.origin.lng)
      });
      addedIds.add(day.origin.id);
    }
    if (day.destination && !addedIds.has(day.destination.id)) {
      locations.push({
        id: day.destination.id,
        name: day.destination.name,
        lat: parseFloat(day.destination.lat),
        lng: parseFloat(day.destination.lng)
      });
      addedIds.add(day.destination.id);
    }
  });

  return locations;
});

const hasOptional = computed(() => visibleTextLength(caseData.value?.optional_trip_information?.description) > 0);

const hasBeforeTrip = computed(() => visibleTextLength(caseData.value?.before_trip_information?.description) > 0);
const hasLuggage = computed(() => visibleTextLength(caseData.value?.luggage_information?.description) > 0);
const hasAfterTrip = computed(() => visibleTextLength(caseData.value?.after_trip_information?.description) > 0);

const hasNeedToKnow = computed(() =>
  hasBeforeTrip.value || hasLuggage.value || hasOptional.value || hasAfterTrip.value
);

const resolveMediaUrl = (image: any): string => {
  if (!image) return "";
  const first = Array.isArray(image) ? image[0] : image;
  return (
    first?.original_url ||
    first?.originalUrl ||
    first?.storageUrl ||
    first?.storage_url ||
    first?.previewUrl ||
    first?.preview_url ||
    first?.url ||
    ""
  );
};

const featuredImageUrl = computed(() => {
  const data = caseData.value;
  if (!data) return "";
  return resolveMediaUrl(
    data.featuredImage ||
      data.featured_image ||
      data.activity?.featuredImage ||
      data.activity?.featured_image
  );
});

const scrollToTop = () => {
  window.scrollTo({ top: 0, behavior: 'smooth' });
};

const handleScroll = () => {
  showScrollTop.value = window.scrollY > 500;
};

const printPage = () => {
  window.print();
};

const contactWhatsApp = () => {
  const caseName = caseData.value.name;
  const caseNumber = caseData.value.number;
  const msg = encodeURIComponent(`Hi! I'm interested in the trip: ${caseName} (Ref: #${caseNumber})`);
  window.open(`https://wa.me/34600000000?text=${msg}`, '_blank');
};

const bookNow = () => {
  const subject = encodeURIComponent(`Booking Request: ${caseData.value.name} (#${caseData.value.number})`);
  const body = encodeURIComponent(`Hi WAW Travels team,\n\nI want to book: ${window.location.href}\n\nThanks!`);
  window.location.href = `mailto:hello@waw.travel?subject=${subject}&body=${body}`;
};

onMounted(async () => {
  window.addEventListener('scroll', handleScroll);
  const caseId = route.params.caseId as string;

  if (!caseId || caseId === 'mock') {
    loading.value = false;
    return;
  }

  try {
    const response = await api.case.casePreview(caseId);
    caseData.value = normalizeCaseData(response.content);
  } catch (error) {
    console.error("Failed to fetch preview data:", error);
  } finally {
    loading.value = false;
  }
});

onUnmounted(() => {
  window.removeEventListener('scroll', handleScroll);
});
</script>

<style lang="scss" scoped>
$brand-pink: #e61275;
$brand-pink-dark: #c90f66;
$brand-gold: #e6b328;
$brand-dark: #222422;
$brand-pink-wash: #ffd0e6;
$brand-gold-wash: #fff6d6;
$brand-included: #1b7a4a;
$brand-included-wash: #e8f6ee;
$brand-excluded: #8a5a00;
$brand-excluded-wash: #fff4e5;
$font-body: 'Manrope', sans-serif;
$font-display: 'Lama Rounded', 'Manrope', sans-serif;

.case-preview-v2 {
  --uq-primary: #FD0C6F;
  --uq-primary-dark: #D00A5B;
  --uq-secondary: #222422;
  --preview-sticky-offset: 0px;
  background-color: #faf8f7;
  min-height: 100vh;
  font-family: $font-body;
  font-size: 16px;
  font-weight: 400;
  line-height: 1.5;
  color: $brand-dark;
  overflow-x: clip;
  -webkit-font-smoothing: antialiased;
  print-color-adjust: exact;
  -webkit-print-color-adjust: exact;

  h1, h2, h3, h4, h5, h6,
  .h1, .h2, .h3, .h4, .h5, .h6 {
    font-family: $font-display;
  }
}

.fw-extra-bold { font-weight: 800; }
.letter-spacing-1 { letter-spacing: 1px; }
.letter-spacing-2 { letter-spacing: 2px; }
.letter-spacing-3 { letter-spacing: 4px; }
.x-small { font-size: 0.65rem; }
.max-w-400 { max-width: 400px; }
.invert-filter { filter: brightness(0) invert(1); }
.bg-light-soft { background-color: #fafafa; }
.text-brand-pink { color: $brand-pink !important; }
.text-brand-muted { color: rgba(34, 36, 34, 0.55); }
.v-divider { width: 1px; height: 24px; background-color: rgba(34, 36, 34, 0.18); }

.preview-body {
  width: 100%;
  max-width: 90rem;
  margin-inline: auto;
  padding-inline: clamp(1rem, 2.4vw, 1.5rem);
  box-sizing: border-box;
}

.preview-toolbar {
  display: none;
}

.preview-hero {
  position: relative;
  overflow: hidden;
  border-bottom: 4px solid $brand-pink;
}

.preview-hero__intro {
  position: relative;
  color: $brand-dark;
  background: linear-gradient(
    165deg,
    #f6f6f6 0%,
    #ffffff 42%,
    rgba(230, 18, 117, 0.08) 100%
  );
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -4rem;
    right: -2rem;
    width: 16rem;
    height: 16rem;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(230, 18, 117, 0.16) 0%, transparent 70%);
    pointer-events: none;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: -3rem;
    left: 8%;
    width: 12rem;
    height: 12rem;
    border-radius: 50%;
    background: radial-gradient(circle, rgba(230, 179, 40, 0.18) 0%, transparent 70%);
    pointer-events: none;
  }
}

.preview-hero__logo-svg {
  position: relative;
  z-index: 1;
  display: inline-block;
  height: clamp(4.5rem, 12vw, 6.75rem);
  width: auto;
  overflow: visible;
}

@media (prefers-reduced-motion: reduce) {
  .preview-hero__logo-svg animateTransform {
    display: none;
  }
}

.preview-hero__kicker {
  position: relative;
  z-index: 1;
  margin: 0;
  font-family: $font-body;
  font-size: clamp(0.9375rem, 2vw, 1.0625rem);
  font-weight: 600;
  letter-spacing: 0;
  line-height: 1.55;
  color: #b0105c;
}

.preview-hero__title {
  position: relative;
  z-index: 1;
  margin: 0;
  font-family: $font-display;
  font-size: clamp(2rem, 5.5vw, 3.25rem);
  font-weight: 900;
  letter-spacing: 0;
  line-height: 1.12;
  color: $brand-dark;
}

.preview-hero__subtitle {
  position: relative;
  z-index: 1;
  margin: 0;
  max-width: 40rem;
  font-family: $font-body;
  font-size: clamp(0.9375rem, 2vw, 1.0625rem);
  font-weight: 500;
  letter-spacing: 0;
  line-height: 1.55;
  color: #3d3d3d;
}

.preview-hero__meta {
  position: relative;
  z-index: 1;
  font-size: 1rem;
  font-weight: 500;
  line-height: 1.5;
  color: $brand-dark;

  .mi {
    color: $brand-pink;
    opacity: 1;
  }
}

.preview-hero__meta-sep {
  opacity: 0.45;
  font-size: 0.85em;
}

.preview-hero__meta--secondary {
  font-size: 0.9375rem;
  font-weight: 600;
  color: #444;
}

.preview-hero__photo {
  position: relative;
  width: 100%;
  height: clamp(220px, 38vw, 420px);
  overflow: hidden;
  background: #f3f3f3;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    filter: none;
  }
}

.preview-hero__actions {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;

  @media (min-width: 768px) {
    align-items: flex-end;
  }
}

.display-mode-toggle {
  display: inline-flex;
  border: 2px solid $brand-dark;
  border-radius: 999px;
  overflow: hidden;
  background: #fff;
}

.display-mode-toggle__btn {
  appearance: none;
  border: 0;
  background: transparent;
  color: $brand-dark;
  font-family: $font-body;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  padding: 10px 16px;
  min-width: 5.5rem;
  cursor: pointer;
  line-height: 1;

  &.is-active {
    background: $brand-dark;
    color: #fff;
  }
}

.preview-print-btn {
  border-radius: 8px !important;
  background-color: $brand-pink !important;
  border-color: $brand-pink !important;
  color: #fff !important;

  &:hover {
    background-color: $brand-pink-dark !important;
    border-color: $brand-pink-dark !important;
  }
}

.preview-loading,
.preview-empty {
  background-color: #fff;
}

.section-title-v1 {
  font-family: $font-display;
  font-weight: 800;
  font-size: clamp(1.5rem, 3vw, 2rem);
  text-transform: none;
  letter-spacing: 0;
  line-height: 1.2;
  color: $brand-dark;
  border-bottom: 2px solid $brand-pink;
  padding-bottom: 16px;
  margin-bottom: 28px;
}

.contact-card {
  border: 1px solid rgba(34, 36, 34, 0.08);
  border-radius: 12px;
  background: #fff;
  box-shadow: 0 8px 24px rgba(34, 36, 34, 0.04);
}

.highlight-card {
  border-radius: 12px;
  overflow: hidden;
  background: #fff;
  box-shadow: 0 8px 24px rgba(34, 36, 34, 0.05);
  border: 1px solid rgba(34, 36, 34, 0.06);
  scroll-margin-top: calc(24px + var(--preview-sticky-offset));
}

.highlight-card__header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
}

.highlight-card__icon {
  font-size: 1.1rem;
}

.highlight-card__title {
  margin: 0;
  font-family: $font-display;
  font-size: 1.25rem;
  font-weight: 800;
  text-transform: none;
  letter-spacing: 0;
  line-height: 1.2;
}

.prose {
  font-family: $font-body;
  font-size: 1rem;
  line-height: 1.5;
  font-weight: 400;
  overflow-wrap: anywhere;

  :deep(img),
  :deep(iframe),
  :deep(table),
  :deep(video) {
    max-width: 100%;
    height: auto;
  }
}

.preview-print-btn,
.cta-btn,
.empty-state-btn {
  font-family: $font-body !important;
  font-weight: 700 !important;
  letter-spacing: 0.03em !important;
}

.highlight-card__body {
  padding: 8px 20px 24px;
}

.highlight-card--before {
  border-left: 5px solid $brand-pink;
  .highlight-card__header { background: $brand-pink-wash; color: $brand-pink-dark; }
}

.highlight-card--luggage {
  border-left: 5px solid $brand-gold;
  .highlight-card__header { background: $brand-gold-wash; color: #8a6a00; }
}

.highlight-card--included {
  border-left: 5px solid $brand-included;
  .highlight-card__header { background: $brand-included-wash; color: $brand-included; }
}

.highlight-card--excluded {
  border-left: 5px solid $brand-gold;
  .highlight-card__header { background: $brand-excluded-wash; color: $brand-excluded; }
}

.highlight-card--optional {
  border-left: 5px solid $brand-pink;
  .highlight-card__header { background: #fff4f8; color: $brand-pink-dark; }
}

.highlight-card--after {
  border-left: 5px solid $brand-dark;
  .highlight-card__header { background: #f3f3f3; color: $brand-dark; }
}

.map-container-v3 {
  width: 100%;
  height: 450px;
  background-color: #f5f5f5;
  border: 1px solid rgba(34, 36, 34, 0.08);
  border-radius: 12px;
  overflow: hidden;
  :deep(.map) { height: 100%; }
}

.day-number { line-height: 1; min-width: 80px; }
.service-img-wrap { aspect-ratio: 16/10; overflow: hidden; background-color: #f5f5f5; }

.resource-row {
  border-left: 3px solid $brand-pink !important;
  border-radius: 8px;
  transition: all 0.2s ease;
  &:hover { background-color: #fff4f8 !important; }
}

.service-type-badge {
  background-color: $brand-pink;
  color: #fff;
  border-radius: 6px;
}

.service-count-badge {
  border: 1px solid rgba(230, 18, 117, 0.35);
  color: $brand-pink;
  background: $brand-pink-wash;
  border-radius: 6px;
}

.carousel-container {
  position: relative;
  overflow: hidden;
  background-color: #f5f5f5;
  border-radius: 12px;
}

.carousel-controls {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  background-color: rgba(34, 36, 34, 0.72);
  backdrop-filter: blur(4px);
  border-radius: 8px 0 12px 0;
}

.carousel-btn {
  background-color: transparent;
  border: none;
  color: #fff;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover {
    transform: scale(1.2);
    color: $brand-gold;
  }
}

.carousel-counter {
  color: #fff;
  font-size: 0.7rem;
  text-align: center;
  min-width: 45px;
  font-weight: 600;
  letter-spacing: 1px;
}

.toc-links {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 20px;
}

.toc-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin: 0;
  padding: 10px 16px;
  border: 1px solid rgba(34, 36, 34, 0.12);
  border-radius: 999px;
  background: #fff;
  color: $brand-dark;
  font-family: $font-body;
  font-size: 0.9375rem;
  font-weight: 600;
  line-height: 1.3;
  cursor: pointer;
  transition: background-color 0.15s ease, border-color 0.15s ease;

  &:hover {
    background: #fff4f8;
    border-color: rgba(230, 18, 117, 0.35);
  }
}

.toc-link__icon {
  color: $brand-pink;
  font-size: 1rem;
}

.toc-days {
  list-style: none;
  margin: 0;
  padding: 0;
  border: 1px solid rgba(34, 36, 34, 0.08);
  border-radius: 16px;
  overflow: hidden;
  background: #fff;
}

.toc-day {
  border-bottom: 1px solid rgba(34, 36, 34, 0.08);

  &:last-child {
    border-bottom: 0;
  }
}

.toc-day__btn {
  width: 100%;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 16px;
  margin: 0;
  padding: 18px 20px;
  border: 0;
  background: transparent;
  text-align: left;
  cursor: pointer;
  font-family: inherit;
  color: inherit;

  &:hover {
    background-color: #fff4f8;
  }
}

.toc-day__num {
  font-family: $font-display;
  font-size: 1.5rem;
  font-weight: 800;
  line-height: 1;
  color: $brand-pink;
  min-width: 2.75rem;
}

.toc-day__content {
  min-width: 0;
}

.toc-day__label {
  display: block;
  margin-bottom: 2px;
  font-family: $font-body;
  font-size: 0.8125rem;
  font-weight: 700;
  color: $brand-pink;
}

.toc-day__route {
  margin: 0;
  font-family: $font-body;
  font-size: 1.125rem;
  font-weight: 700;
  line-height: 1.35;
  color: $brand-dark;
}

.toc-day__arrow {
  display: inline-block;
  margin: 0 6px;
  font-size: 0.85em;
  opacity: 0.45;
  vertical-align: middle;
}

.toc-day__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 6px 14px;
  margin-top: 6px;
  font-family: $font-body;
  font-size: 0.9375rem;
  font-weight: 500;
  color: #3d3d3d;
}

.toc-day__chevron {
  color: rgba(34, 36, 34, 0.35);
  font-size: 1.1rem;
}

.need-to-know-inclusions {
  scroll-margin-top: calc(24px + var(--preview-sticky-offset));
}

.day-label {
  color: $brand-pink;
  font-size: 0.8125rem;
  letter-spacing: 0.06em;
}

.stage-length {
  margin-left: auto;
  padding-right: 16px;
}

.day-accordion {
  border: 1px solid rgba(34, 36, 34, 0.08);
  border-radius: 12px;
  overflow: hidden;
  scroll-margin-top: calc(24px + var(--preview-sticky-offset));
}

.day-accordion-header {
  border-bottom: 1px solid rgba(34, 36, 34, 0.06);
  transition: all 0.2s ease;
  min-height: 52px;
  &:hover { background-color: #fff4f8; }
}

.day-accordion-header__route {
  overflow-wrap: anywhere;
}

.day-accordion-header__meta {
  font-size: 0.875rem;
  font-weight: 500;
  color: #4a4a4a;
}

.location-guide-header {
  background-color: #fafafa;
  border-bottom: 1px solid rgba(34, 36, 34, 0.06);
  transition: all 0.2s ease;
  min-height: 52px;
  &:hover { background-color: #fff4f8; }
}

.location-guide-header__title {
  font-family: $font-display;
  font-size: 1rem;
  line-height: 1.3;
  overflow-wrap: anywhere;
}

.location-info-block {
  border: 1px solid rgba(34, 36, 34, 0.08);
  border-left: 4px solid $brand-gold !important;
  border-radius: 12px;
  margin-top: 48px !important;
  overflow: hidden;
}

.service-notes {
  border-left-color: $brand-pink !important;
}

.cta-banner-v1 {
  background: $brand-dark;
  border-radius: 16px;
  border-top: 4px solid $brand-pink;
  box-shadow: 0 24px 48px rgba(34, 36, 34, 0.12);
}

.cta-banner-v1__title {
  font-family: $font-display;
  font-size: clamp(1.5rem, 5vw, 2.5rem);
  letter-spacing: 0;
  text-transform: none;
  line-height: 1.15;
}

.cta-banner-v1__text {
  font-size: 1rem;
  line-height: 1.5;
}

.footer-v1 {
  background-color: $brand-dark;
  border-top: 4px solid $brand-gold;
}

.footer-v1__links {
  font-size: 0.8125rem;
  letter-spacing: 0.08em;
  opacity: 0.7;
  flex-wrap: wrap;
}

.cta-btn {
  border-radius: 8px !important;
}

.cta-btn--primary {
  background-color: $brand-pink !important;
  border-color: $brand-pink !important;
  color: #fff !important;
}

.back-to-top-v1 {
  position: fixed;
  bottom: 30px;
  right: 30px;
  width: 45px;
  height: 45px;
  background-color: $brand-pink;
  color: #fff;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 1040;
  transition: all 0.3s ease;
  &:hover {
    transform: translateY(-5px);
    background-color: $brand-pink-dark;
  }
}

.empty-state-btn {
  border-radius: 8px !important;
  background-color: $brand-pink !important;
  border-color: $brand-pink !important;
}

:deep(.btn-primary) {
  background-color: $brand-pink !important;
  border-color: $brand-pink !important;
  color: #fff !important;
}

@media screen {
  .case-preview-v2--dark {
    --bs-heading-color: #fff;
    --bs-body-color: #fff;
    --bs-body-bg: #000;
    --bs-secondary-color: #fff;
    background-color: #000;
    color: #fff;
    color-scheme: dark;

    h1, h2, h3, h4, h5, h6,
    .h1, .h2, .h3, .h4, .h5, .h6,
    .highlight-card__title,
    .highlight-card__icon,
    .highlight-card__header,
    .section-title-v1,
    .location-guide-header__title {
      color: #fff !important;
    }

    .preview-loading,
    .preview-empty,
    main,
    .bg-white,
    .bg-light-soft {
      background-color: #000 !important;
      color: #fff;
    }

    .preview-hero {
      border-bottom-color: #fff;
    }

    .preview-hero__intro {
      color: #fff;
      background: #000;

      &::before,
      &::after {
        display: none;
      }
    }

    .preview-hero__logo-svg path {
      fill: #fff !important;
    }

    .preview-hero__logo-svg animateTransform {
      display: none;
    }

    .preview-toolbar {
      background: rgba(0, 0, 0, 0.94);
      border-bottom-color: rgba(255, 255, 255, 0.25);
    }

    .preview-hero__kicker,
    .preview-hero__title,
    .preview-hero__subtitle,
    .preview-hero__meta,
    .preview-hero__meta--secondary {
      color: #fff;
    }

    .preview-hero__meta .mi {
      color: #fff;
    }

    .v-divider {
      background-color: #fff;
    }

    .preview-hero__photo {
      background: #111;
    }

    .display-mode-toggle {
      border-color: #fff;
      background: #000;
    }

    .display-mode-toggle__btn {
      color: #fff;

      &.is-active {
        background: #fff;
        color: #000;
      }
    }

    .preview-print-btn {
      background-color: #fff !important;
      border-color: #fff !important;
      color: #000 !important;

      &:hover {
        background-color: #e6e6e6 !important;
        border-color: #e6e6e6 !important;
      }
    }

    .text-brand-pink,
    .text-brand-muted,
    .text-muted,
    .text-dark,
    .day-label {
      color: #fff !important;
    }

    .section-title-v1 {
      border-bottom-color: #fff;
    }

    .contact-card,
    .highlight-card,
    .toc-days,
    .day-accordion,
    .location-info-block,
    .map-container-v3 {
      background: #111;
      border-color: #fff;
      box-shadow: none;
      color: #fff;
    }

    .highlight-card__header {
      background: #1a1a1a !important;
      color: #fff !important;
    }

    .highlight-card--before,
    .highlight-card--luggage,
    .highlight-card--included,
    .highlight-card--excluded,
    .highlight-card--optional,
    .highlight-card--after {
      border-left-color: #fff;
    }

    .location-info-block,
    .service-notes,
    .resource-row {
      border-left-color: #fff !important;
    }

    .toc-link {
      background: #111;
      border-color: #fff;
      color: #fff;
    }

    .toc-link__icon,
    .toc-day__num,
    .toc-day__label,
    .toc-day__route,
    .toc-day__meta,
    .toc-day__chevron,
    .day-accordion-header__meta {
      color: #fff;
    }

    .toc-day {
      border-bottom-color: rgba(255, 255, 255, 0.25);
    }

    .toc-day__btn:hover,
    .toc-link:hover {
      background-color: #222;
    }

    .day-accordion-header,
    .location-guide-header {
      background-color: #111;
      border-bottom-color: rgba(255, 255, 255, 0.25);
      color: #fff;

      &:hover {
        background-color: #222;
      }
    }

    .resource-row:hover {
      background-color: #222 !important;
    }

    .service-type-badge {
      background-color: #fff;
      color: #000;
    }

    .service-count-badge {
      border-color: #fff;
      color: #fff;
      background: #000;
    }

    .carousel-controls {
      background-color: rgba(0, 0, 0, 0.85);
    }

    .carousel-btn:hover {
      color: #fff;
    }

    .cta-banner-v1 {
      background: #000;
      border: 1px solid #fff;
      border-top: 4px solid #fff;
      box-shadow: none;
    }

    .cta-btn--primary,
    .empty-state-btn,
    :deep(.btn-primary) {
      background-color: #fff !important;
      border-color: #fff !important;
      color: #000 !important;
    }

    .footer-v1 {
      background-color: #000;
      border-top-color: #fff;
    }

    .back-to-top-v1 {
      background-color: #fff;
      color: #000;

      &:hover {
        background-color: #e6e6e6;
      }
    }

    hr {
      border-color: #fff;
      opacity: 0.35;
    }

    .prose,
    .prose :deep(*) {
      color: #fff !important;
    }

    .prose :deep(a) {
      color: #fff !important;
      text-decoration: underline;
    }
  }
}

@media print {
  .hide-print { display: none !important; }
  .preview-hero {
    color: $brand-dark !important;
    print-color-adjust: exact;
  }
  .preview-hero__logo-svg { print-color-adjust: exact; }
  .preview-hero__photo { height: 240px !important; }
  .day-detail-block, .highlight-card, .day-accordion { break-inside: avoid; }
  .highlight-card--before .highlight-card__header,
  .highlight-card--luggage .highlight-card__header,
  .highlight-card--included .highlight-card__header,
  .highlight-card--excluded .highlight-card__header {
    print-color-adjust: exact;
  }
  .bg-light-soft { background-color: #f8f9fa !important; print-color-adjust: exact; }
  section { margin-bottom: 32px !important; }
}

@media (max-width: 767.98px) {
  .case-preview-v2 {
    --preview-sticky-offset: 56px;
    font-size: 16px;
  }

  .preview-toolbar {
    display: flex;
    position: sticky;
    top: 0;
    z-index: 1050;
    justify-content: flex-end;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    padding-top: max(8px, env(safe-area-inset-top));
    background: rgba(255, 255, 255, 0.94);
    backdrop-filter: blur(10px);
    border-bottom: 1px solid rgba(34, 36, 34, 0.08);
  }

  .preview-hero__theme-toggle {
    display: none;
  }

  .preview-hero__actions {
    align-items: stretch;
    width: 100%;
  }

  .preview-print-btn {
    width: 100%;
    min-height: 44px;
  }

  .display-mode-toggle__btn {
    min-height: 44px;
    min-width: 4.75rem;
    padding: 12px 14px;
    font-size: 0.8125rem;
  }

  .preview-hero__title,
  .preview-hero__subtitle,
  .preview-hero__meta,
  .toc-day__route,
  .day-accordion-header__route {
    overflow-wrap: anywhere;
  }

  .preview-hero__meta {
    gap: 8px !important;
  }

  .v-divider {
    display: none;
  }

  .preview-hero__logo-svg {
    height: 3.5rem;
  }

  .preview-hero__photo {
    height: clamp(180px, 52vw, 280px);
  }

  .section-title-v1 {
    font-size: 1.375rem;
    padding-bottom: 12px;
    margin-bottom: 20px;
  }

  .contact-card {
    padding: 16px !important;
  }

  .highlight-card__header {
    padding: 14px 16px;
  }

  .highlight-card__body {
    padding: 4px 16px 20px;
  }

  .highlight-card__title {
    font-size: 1.0625rem;
  }

  .toc-link {
    min-height: 44px;
    padding: 10px 14px;
    font-size: 0.875rem;
  }

  .toc-day__btn {
    grid-template-columns: auto minmax(0, 1fr);
    gap: 12px;
    padding: 14px 14px;
    min-height: 64px;
  }

  .toc-day__num {
    font-size: 1.25rem;
    min-width: 2.25rem;
  }

  .toc-day__route {
    font-size: 1rem;
  }

  .toc-day__chevron {
    display: none;
  }

  .map-container-v3 {
    height: 240px;
    border-radius: 10px;
  }

  .stage-length {
    margin-left: 0;
    padding-right: 0;
  }

  .location-info-block {
    margin-top: 24px !important;
  }

  .carousel-controls {
    padding: 8px 12px;
    gap: 16px;
  }

  .carousel-btn {
    padding: 8px;
    font-size: 1.25rem;
  }

  .cta-banner-v1__actions {
    flex-direction: column;
    align-items: stretch;
  }

  .cta-btn {
    width: 100%;
    min-height: 48px;
  }

  .footer-v1 {
    padding-top: 32px;
    padding-bottom: calc(32px + env(safe-area-inset-bottom));
  }

  .back-to-top-v1 {
    bottom: calc(16px + env(safe-area-inset-bottom));
    right: 16px;
    width: 44px;
    height: 44px;
  }
}
</style>
